import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TreeModule } from 'primeng/tree';
import { ButtonModule } from 'primeng/button';
import { TreeNode } from 'primeng/api';
import { TranslocoModule } from '@jsverse/transloco';
import { ZipExplorerService } from '../../services/zip-explorer.service';
import { IZipTreeNode } from '../../model/zip-tree-node';
import { ZIP_PASSWORD_CONFIG } from '../../constants/zip-password.config';
import { PdfViewer } from '../pdf-viewer/pdf-viewer';
import { PrintFlowService } from '../../services/print-flow.service';
import { PrinterPickerComponent } from '../printer-picker/printer-picker';

/**
 * ZIP faylni (masalan bir bosqichning barcha javob varaqalari) diskka
 * saqlamasdan, brauzerda ochib, papka daraxti orqali ko'rsatadi. Tanlangan
 * fayl faqat ko'rish/pechat uchun ochiladi — xom ZIP yoki PDF hech qachon
 * yuklab olish (download) sifatida taqdim etilmaydi.
 */
@Component({
  selector: 'lib-zip-pdf-explorer',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    TreeModule,
    ButtonModule,
    TranslocoModule,
    PdfViewer,
    PrinterPickerComponent,
  ],
  templateUrl: './zip-pdf-explorer.html',
  styleUrl: './zip-pdf-explorer.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZipPdfExplorerComponent implements OnDestroy {
  private readonly zipExplorerService = inject(ZipExplorerService);
  private readonly printFlow = inject(PrintFlowService);
  private previewBlob: Blob | null = null;
  readonly printerPicker = viewChild(PrinterPickerComponent);

  visible = signal(false);
  title = signal('');
  isLoadingTree = signal(false);
  isLoadingFile = signal(false);
  treeNodes = signal<TreeNode<IZipTreeNode>[]>([]);
  selectedFileName = signal<string>('');
  previewUrl = signal<string | null>(null);
  errorKey = signal<string | null>(null);
  selectedPath = signal('');
  isPrinting = signal(false);

  /** Diskka tushirilmasdan, xotiradagi ZIP Blob'ni ochib, daraxtni chiqaradi. */
  async open(zipBlob: Blob, title = ''): Promise<void> {
    this.reset();
    this.title.set(title);
    this.visible.set(true);
    this.isLoadingTree.set(true);

    try {
      const tree = await this.zipExplorerService.readTree(
        zipBlob,
        this.resolvePassword(),
      );
      this.treeNodes.set(this.mapToTreeNodes(tree));
    } catch {
      this.errorKey.set('zip_open_error');
    } finally {
      this.isLoadingTree.set(false);
    }
  }

  close(): void {
    this.visible.set(false);
    this.reset();
  }

  async print(): Promise<void> {
    const blob = this.previewBlob;
    const picker = this.printerPicker();
    const printer = picker?.selectedPrinter();
    if (!blob || !picker || !printer || this.isPrinting()) {
      return;
    }

    this.isPrinting.set(true);
    try {
      await this.printFlow.print({
        blob,
        fileName: this.selectedFileName() || 'document.pdf',
        docKey: `zip:${this.title()}:${this.selectedPath()}`,
        printer,
        printers: picker.printers(),
      });
    } finally {
      this.isPrinting.set(false);
    }
  }

  async onNodeSelect(event: { node: TreeNode<IZipTreeNode> }): Promise<void> {
    const data = event.node?.data;
    if (!data || data.isFolder || !data.entry) {
      return;
    }

    this.selectedFileName.set(data.name);
    this.selectedPath.set(data.path);
    this.revokePreviewUrl();
    this.previewUrl.set(null);
    this.previewBlob = null;
    this.errorKey.set(null);
    this.isLoadingFile.set(true);

    try {
      const blob = await this.zipExplorerService.extractFile(
        data.entry,
        this.resolvePassword(),
      );
      this.previewBlob = blob;
      this.previewUrl.set(URL.createObjectURL(blob));
    } catch {
      this.errorKey.set('zip_file_open_error');
    } finally {
      this.isLoadingFile.set(false);
    }
  }

  private resolvePassword(): string | null {
    return ZIP_PASSWORD_CONFIG.enabled
      ? ZIP_PASSWORD_CONFIG.getPassword()
      : null;
  }

  private mapToTreeNodes(nodes: IZipTreeNode[]): TreeNode<IZipTreeNode>[] {
    return nodes.map((node) => ({
      key: node.path,
      label: node.name,
      data: node,
      leaf: !node.isFolder,
      icon: node.isFolder ? 'pi pi-folder' : 'pi pi-file-pdf',
      children: node.children ? this.mapToTreeNodes(node.children) : undefined,
    }));
  }

  private reset(): void {
    this.treeNodes.set([]);
    this.selectedFileName.set('');
    this.selectedPath.set('');
    this.errorKey.set(null);
    this.revokePreviewUrl();
    this.previewUrl.set(null);
    this.previewBlob = null;
  }

  private revokePreviewUrl(): void {
    const url = this.previewUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }
}
